import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  LoadingBlock,
  RoleBadge,
  Select,
  buttonStyles,
} from "../components/ui";
import { Trans, chooseLocale, useTranslation } from "../i18n";
import { LANGUAGE_NAMES, LOCALES } from "../i18n/locale";
import { ApiError, api } from "../lib/api";
import { absoluteDay } from "../lib/format";
import { initials } from "../lib/team";
import type { User } from "../lib/types";

/**
 * Your own account, as opposed to the organization you happen to be looking at.
 *
 * Reached from the user menu beside Security, and the split between the two
 * places is the same one that page already draws: what belongs to the person
 * signed in reaches the menu under their name, and what belongs to an
 * organization lives in the nav rail with the rest of it. Somebody in three
 * organizations has one profile and three sets of billing details, so putting
 * the second here would make a page whose meaning changed with the switcher.
 *
 * Only the display name can be changed, and what that excludes is the point:
 *
 *   email     the login identity and where a password reset is delivered.
 *             Changing it from an already-open session would move the account
 *             to an address nobody has proved they control, so it needs a
 *             verification flow rather than a text field.
 *   password  changes through the reset flow, which proves the mailbox.
 *   2FA       has its own routes, each re-checking the password.
 */
export function ProfilePage() {
  const { user, reloadSession, organizations } = useAuth();
  const { t } = useTranslation("profile");

  /*
   * Seeded at construction as well as in the effect below. The effect alone
   * left the field empty for one paint whenever the session was already
   * loaded — brief, and it reads as a form that lost what was in it.
   */
  const [name, setName] = useState(user?.name ?? "");
  const [baseline, setBaseline] = useState(user?.name ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [savingLocale, setSavingLocale] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name);
    setBaseline(user.name);
  }, [user]);

  if (!user) return <LoadingBlock />;

  const dirty = name.trim() !== baseline && name.trim() !== "";

  async function save() {
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      await api<User>("/v1/auth/me", { method: "PATCH", body: { name: name.trim() } });
      // The name appears in the user menu, which reads the session rather than
      // this page — so the reload is what makes the change visible where
      // somebody will look for it.
      await reloadSession();
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  /*
   * Saved on the account and applied at once. The cookie is written too, so the
   * marketing site and the signed-out screens agree with the choice; "automatic"
   * clears both and hands the decision back to detection.
   */
  async function saveLocale(value: string) {
    setError(null);
    setSaved(false);
    setSavingLocale(true);
    const locale = value === "" ? null : value;
    try {
      await api<User>("/v1/auth/me", { method: "PATCH", body: { locale } });
      await chooseLocale(locale);
      await reloadSession();
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("saveFailed"));
    } finally {
      setSavingLocale(false);
    }
  }

  return (
    <div className="space-y-6">
      {error && <Alert tone="error">{error}</Alert>}
      {saved && !dirty && <Alert tone="success">{t("saved")}</Alert>}

      {/*
        An identity band before any form, so the page has a subject rather than
        opening on an empty text box. The initials are untinted for the reason
        the team list records: mint, amber and blue already mean live, trouble
        and navigable three rows away, and an avatar wearing one of them makes
        both decorations.
      */}
      <Card>
        <div className="flex flex-wrap items-center gap-4 p-5">
          <span
            aria-hidden="true"
            className="grid size-12 shrink-0 place-items-center rounded-md bg-raised text-[15px] font-medium text-fg-muted"
          >
            {initials(user.name)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[15px] text-fg">{user.name}</p>
            <p className="truncate text-[13px] text-fg-subtle">{user.email}</p>
          </div>
          <div className="ms-auto flex flex-wrap items-center gap-x-6 gap-y-1 text-[13px]">
            <span className="flex items-baseline gap-2">
              <span className="text-fg-subtle">{t("memberSince")}</span>
              <span className="text-fg">{absoluteDay(user.createdAt)}</span>
            </span>
            <span className="flex items-baseline gap-2">
              <span className="text-fg-subtle">{t("organizations")}</span>
              <span className="text-fg tabular-nums">{organizations.length}</span>
            </span>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title={t("details.title")} description={t("details.description")} />
        <div className="space-y-5 p-5">
          {/*
            Held to a readable measure. A name is a handful of characters and a
            field stretched to the width of the page invites the reader to
            expect one, which is the only thing an input's width communicates.
          */}
          <div className="max-w-md space-y-5">
            <Field
              label={t("details.name")}
              htmlFor="profile-name"
              hint={t("details.nameHint")}
            >
              <Input
                id="profile-name"
                value={name}
                maxLength={120}
                autoComplete="name"
                onChange={(event) => {
                  setSaved(false);
                  setName(event.target.value);
                }}
              />
            </Field>

            {/*
              Shown and not editable, with the reason given. A disabled field
              with no explanation reads as something broken; the sentence turns
              it into a decision somebody made.
            */}
            <Field
              label={t("details.email")}
              htmlFor="profile-email"
              hint={t("details.emailHint")}
            >
              <Input id="profile-email" value={user.email} disabled readOnly />
            </Field>
          </div>
        </div>

        {/*
          The actions sit on their own footer rather than inside the field
          stack, so the boundary between "what I am editing" and "what happens
          next" is drawn rather than implied by spacing alone.
        */}
        <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button variant="ghost" size="sm" disabled={!dirty || saving} onClick={() => setName(baseline)}>
            {t("details.discard")}
          </Button>
          <Button size="sm" disabled={!dirty} loading={saving} onClick={() => void save()}>
            {t("details.save")}
          </Button>
        </div>
      </Card>

      <Card>
        <CardHeader title={t("language.title")} description={t("language.description")} />
        <div className="max-w-md p-5">
          <Field label={t("language.field")} htmlFor="profile-locale" hint={t("language.automaticHint")}>
            <Select
              id="profile-locale"
              value={user.locale ?? ""}
              disabled={savingLocale}
              onChange={(event) => void saveLocale(event.target.value)}
            >
              <option value="">{t("language.automatic")}</option>
              {LOCALES.map((locale) => (
                <option key={locale} value={locale} lang={locale}>
                  {LANGUAGE_NAMES[locale]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      <Card>
        <CardHeader title={t("signingIn.title")} description={t("signingIn.description")} />
        <div className="flex flex-wrap items-center justify-between gap-3 p-5">
          <span className="flex items-center gap-2.5 text-[13px]">
            {/* Colour and a dot, which is how state reads everywhere else here —
                a word alone makes the reader parse a sentence to learn a fact. */}
            <span
              aria-hidden="true"
              className={`size-1.5 rounded-full ${user.totpEnabled ? "bg-mint" : "bg-fg-subtle"}`}
            />
            <span className="text-fg">{t("signingIn.twoFactor")}</span>
            <span className={user.totpEnabled ? "text-mint" : "text-fg-subtle"}>
              {user.totpEnabled ? t("signingIn.on") : t("signingIn.off")}
            </span>
          </span>
          {/* A Link wearing the button styles rather than a Button wrapping a
              Link: this navigates, and `buttonStyles` exists so an anchor can
              look like one without nesting an interactive element inside one. */}
          <Link to="/settings/security" className={buttonStyles("secondary", "sm")}>
            {user.totpEnabled ? t("signingIn.manage") : t("signingIn.turnOn")}
          </Link>
        </div>
      </Card>

      {organizations.length > 0 && (
        <Card>
          <CardHeader title={t("memberships.title")} description={t("memberships.description")} />
          <ul className="divide-y divide-line">
            {organizations.map((org) => (
              <li key={org.id} className="flex items-center justify-between gap-4 px-5 py-3">
                <span className="min-w-0 truncate text-[13px] text-fg">{org.name}</span>
                <RoleBadge role={org.role} />
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/*
        Named here because this is where somebody comes looking for it, and
        explained because the answer to "why is it not on my profile" is a real
        one rather than an oversight.
      */}
      <p className="text-xs leading-6 text-fg-subtle">
        <Trans
          t={t}
          i18nKey="billingNote"
          components={{
            billing: <Link to="/settings/billing-details" className="text-accent underline underline-offset-2" />,
          }}
        />
      </p>
    </div>
  );
}
